import React, { useState } from "react";
import { useMsal, AuthenticatedTemplate, UnauthenticatedTemplate } from "@azure/msal-react";
import { loginRequest } from "./authConfig";
import Navbar from "./components/NavBar";
import MainContent from "./components/MainContent";

const App: React.FC = () => {
  const { instance } = useMsal();
  const [activePage, setActivePage] = useState("Home");

  const handleLogin = () => {
    instance.loginRedirect(loginRequest);
  };

  return (
    <>
      <AuthenticatedTemplate>
        <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-blue-50 to-purple-50">
          <Navbar onNavigate={setActivePage} activePage={activePage} />
          <div className="max-w-9xl mx-auto px-4 pb-8">
            <MainContent activePage={activePage} onNavigate={setActivePage} />
          </div>
        </div>
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <div className="flex items-center justify-center min-h-screen bg-gray-50">
          <div className="flex w-full max-w-3xl rounded-xl border border-gray-200 overflow-hidden shadow-sm bg-white">
            
            {/* Left panel */}
            <div className="flex flex-col justify-between w-1/2 bg-gray-50 border-r border-gray-200 p-10">
              {/* Logo */}
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-[#3d3fd4] flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <polygon points="12 2 22 8.5 22 15.5 12 22 2 15.5 2 8.5" />
                  </svg>
                </div>
                <span className="text-base font-medium text-gray-900">OpsNest</span>
              </div>

              {/* Features */}
              <div>
                <h2 className="text-xl font-medium text-gray-900 leading-snug mb-2">
                  Operations, centralized.<br />Teams, unblocked.
                </h2>
                <p className="text-sm text-gray-500 mb-7 leading-relaxed">
                  One place for releases, deployments, configs, and more.
                </p>
                <ul className="flex flex-col gap-3">
                  {[
                    { icon: "🚀", label: "Plan, promote, and ship releases across every repo" },
                    { icon: "🗂️", label: "Track tasks and project status in real time" },
                    { icon: "☸️", label: "See ArgoCD deployment health at a glance" },
                    { icon: "📈", label: "Surface Grafana dashboards without switching tabs" },
                    { icon: "🔑", label: "Manage secrets and app config securely" },
                  ].map(({ icon, label }) => (
                    <li key={label} className="flex items-center gap-3 text-sm text-gray-600">
                      <div className="w-7 h-7 rounded-lg bg-[#3d3fd4] flex items-center justify-center text-white text-xs shrink-0">
                        {icon}
                      </div>
                      {label}
                    </li>
                  ))}
                </ul>
              </div>

              <p className="text-xs text-gray-400">© 2026 OutThink. All rights reserved.</p>
            </div>

            {/* Right panel */}
            <div className="flex flex-col justify-center w-1/2 p-10">
              <p className="text-xs font-medium tracking-widest text-gray-400 uppercase mb-1">Welcome back</p>
              <h1 className="text-xl font-medium text-gray-900 mb-1">Sign in to OpsNest</h1>
              <p className="text-sm text-gray-500 mb-8 leading-relaxed">
                Use your company account to access your workspace.
              </p>
              <button
                onClick={handleLogin}
                className="flex items-center justify-center gap-2.5 w-full py-2.5 px-4 rounded-lg border border-gray-200 bg-white text-sm font-medium text-gray-800 hover:bg-gray-50 transition-colors"
              >
                {/* Microsoft logo */}
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="1" y="1" width="7.5" height="7.5" fill="#f25022"/>
                  <rect x="9.5" y="1" width="7.5" height="7.5" fill="#7fba00"/>
                  <rect x="1" y="9.5" width="7.5" height="7.5" fill="#00a4ef"/>
                  <rect x="9.5" y="9.5" width="7.5" height="7.5" fill="#ffb900"/>
                </svg>
                Sign in with Microsoft
              </button>
            </div>

          </div>
        </div>
      </UnauthenticatedTemplate>
    </>
  );
};

export default App;
